import { useRef } from 'react';
import type { CSSProperties } from 'react';
import { Eye, EyeOff, Pencil, Pin, PinOff, Trash2 } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import type { Conversation } from '../../../lib/conversations';
import { useConversationActions, CONVERSATIONS_PAGE } from '../../../lib/use-conversation-actions';
import { setRecapVisibleFor } from '../../../lib/recap-preference';
import { useRecapVisible } from '../../../lib/use-recap-visible';
import { DeleteConversationConfirm } from '../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../conversations/RenameConversationDialog';
import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { AgentsIslandBadge } from '../AgentsIslandBadge';
import { IconButton } from '../ui';
import { OverflowMenu, type OverflowMenuAction } from '../ui/OverflowMenu';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { headerStyle, newChatTitleStyle } from './styles';
import styles from './ChatHeader.module.css';
import { CloseIcon, ExpandIcon, FilesIcon, NewConversationIcon, ShrinkIcon } from './icons';

export interface ChatHeaderProps {
  narrow: boolean;
  fullscreen: boolean;
  isNewChat: boolean;
  headerTitle: string | undefined;
  onNewConversation: () => void;
  onCompose: () => void;
  onExpand: () => void;
  onShrink: () => void;
  onMinimize: () => void;
  // Opens the per-chat Files/Media panel. Undefined => no listable conversation
  // yet (a brand-new, never-sent chat), so the button is hidden.
  onOpenFiles?: () => void;
  /** The conversation this header is for. Undefined on a brand-new chat, which has no row to act on yet —
   *  so the pin/rename/delete items are simply absent rather than present and inert. */
  conversation?: Conversation;
}

// Mobile: the ⋮ overflow rides a 36px white circle, matching the BrandHeader hamburger
// (Figma "Header" 14701:127253 — Mobile Web Default). The 30px ghost trigger centres inside it.
const MOBILE_MORE_CIRCLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'var(--ds-surface-sunken)',
};

export function ChatHeader({
  narrow,
  fullscreen,
  isNewChat,
  headerTitle,
  onNewConversation,
  onCompose,
  onExpand,
  onShrink,
  onMinimize,
  onOpenFiles,
  conversation,
}: ChatHeaderProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  const recapVisible = useRecapVisible(conversation?.id);
  // The overflow menu hangs from the header's bottom edge (Figma), not the trigger's — so it clears the
  // header by the same amount whether the trigger is the 30px desktop button or the 36px mobile circle.
  const headerRef = useRef<HTMLElement>(null);

  // Secondary actions live in the 3-dots overflow so the header stays clean (Figma "Header"
  // 14701:127253). On WIDE the menu holds New Chat (+ Files) and the row keeps a standalone expand +
  // close beside it. On NARROW there is no standalone new-chat icon either, so New Chat lives in the
  // menu too. New Chat appears once there's a conversation to leave; Files once the chat is listable.
  const menuActions: OverflowMenuAction[] = [];
  if (!isNewChat) {
    menuActions.push({
      key: 'new-chat',
      label: t('chat.newConversation'),
      icon: <NewConversationIcon />,
      onSelect: () => {
        logPageClickBi({ page_name: 'chat', button_name: 'new_chat' });
        // On narrow the compose action opens a fresh chat in place; on wide it starts a new conversation.
        (narrow ? onCompose : onNewConversation)();
      },
    });
  }
  if (onOpenFiles) {
    menuActions.push({
      key: 'chat-files',
      label: t('chatFiles.title'),
      icon: <FilesIcon />,
      onSelect: () => {
        logPageClickBi({ page_name: 'chat', button_name: 'open_chat_files' });
        onOpenFiles();
      },
    });
  }
  // The same three actions the conversation's row in the sidebar offers, for the conversation you are
  // actually IN — reached here because that is where a user thinks about the chat they are reading, and
  // because the mobile header is the only chrome on that screen. The labels, order and destructive skin
  // match the row menu exactly; the writes are the same shared flow, so the two cannot diverge.
  // Guard on the ID, not the conversation: a row with no server id cannot be pinned, renamed or deleted —
  // every one of those writes addresses it by id — and guarding on the id is also what narrows it to a string.
  const convId = conversation?.id;
  if (convId) {
    // Explicit final fallback: `t` is typed as possibly-undefined, and the dialogs' target takes a real
    // string — a title is what those dialogs put in front of the user, so it must never be blank.
    const title: string = conversation?.name || t('common.untitled') || '';
    const pinned = Boolean(conversation?.pinned);
    menuActions.push(
      {
        key: 'pin',
        label: pinned ? t('conversations.unpin') : t('conversations.pin'),
        icon: pinned ? <PinOff size={16} aria-hidden /> : <Pin size={16} aria-hidden />,
        onSelect: () => void flow.togglePin({ id: convId, pinned }),
      },
      {
        key: 'rename',
        label: t('conversations.rename.action'),
        icon: <Pencil size={16} aria-hidden />,
        onSelect: () => flow.requestRename({ id: convId, title }),
      },
      // The way back from the recap banner's ×, which hides the line on THIS conversation. It names the
      // outcome the way the pin item does, so the label is what pressing it will do.
      {
        key: 'recap',
        label: recapVisible ? t('chat.recap.hide') : t('chat.recap.show'),
        icon: recapVisible ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />,
        onSelect: () => {
          logPageClickBi({ page_name: 'chat', button_name: recapVisible ? 'disable_recap' : 'enable_recap' });
          setRecapVisibleFor(convId, !recapVisible);
        },
      },
      {
        key: 'delete',
        label: t('conversations.delete.action'),
        icon: <Trash2 size={16} aria-hidden />,
        destructive: true,
        onSelect: () => flow.requestDelete({ id: convId, title }),
      },
    );
  }

  return (
    <>
    <header
      ref={headerRef}
      style={{
        ...headerStyle,
        // The centred island below is absolute; without this it would resolve against the fixed chat surface
        // and centre on the whole panel rather than on this row.
        position: 'relative',
        ...(narrow || isNewChat ? { borderBottom: 'none' } : null),
        ...(narrow ? { justifyContent: 'flex-end' } : null),
      }}
    >
      {narrow ? null : <span style={newChatTitleStyle}>{headerTitle}</span>}

      {/* CENTRED, whenever the chat COVERS the screen — mirroring native's chat header, which centres the
          island and draws it before the overflow menu. That is the one case needing its own copy: the chat
          surface is a fixed z-50 layer, so AppFrame's absolute z-6 top-bar island is covered by it and cannot
          be seen at all (which is why those modes used to have none). On a phone every open chat covers the
          screen — `fullscreen` is a wide-only mode — so narrow counts too. Docked chat keeps using the top-bar
          island; rendering here as well would put TWO islands on screen and register the chip slot twice. */}
      {(narrow || fullscreen) && (
        // Wide has room to centre on the whole row. A phone does not: the BrandHeader hamburger occupies the
        // top-left, so the island takes the leftover space as a flex item and centres inside THAT, clearing
        // the 44px hamburger with paddingInlineStart.
        <div
          style={
            narrow
              ? { flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', paddingInlineStart: 44 }
              : { position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex' }
          }
        >
          <GenericEventsRouteProvider value="agents-island">
            <AgentsIslandBadge side="center" />
          </GenericEventsRouteProvider>
        </div>
      )}
      {/* Right cluster. Narrow shows ONLY the overflow (New Chat/Files live inside it); wide keeps the
          standalone expand/shrink + close beside the overflow. Figma gap 4. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {menuActions.length > 0 &&
          (narrow ? (
            // Mobile: the ⋮ sits on a white circle like the hamburger (Figma "Mobile Web Default"). The
            // centered agents island is right beside this right-aligned menu on a narrow screen, so give
            // the menu a little clearance below the header rather than hanging it flush.
            <span style={MOBILE_MORE_CIRCLE}>
              <OverflowMenu actions={menuActions} ariaLabel={t('chat.moreActions')} triggerClassName={styles.triggerNoHover} blockAnchorRef={headerRef} blockAnchorGap={8} />
            </span>
          ) : (
            // Wide: the ⋮ hovers like the expand/close buttons beside it and wears a selected fill while
            // its menu is open (Figma "Chat Header" 14692:214629).
            <OverflowMenu actions={menuActions} ariaLabel={t('chat.moreActions')} triggerClassName={styles.triggerSelectable} blockAnchorRef={headerRef} />
          ))}
        {!narrow &&
          (fullscreen ? (
            <IconButton size="small" onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'shrink_chat' }); onShrink(); }} aria-label={t('chat.float')} title={t('chat.floatShort')}>
              <ShrinkIcon />
            </IconButton>
          ) : (
            <IconButton size="small" onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'expand_chat' }); onExpand(); }} aria-label={t('chat.expand')} title={t('chat.expandShort')}>
              <ExpandIcon />
            </IconButton>
          ))}
        {!narrow && (
          <IconButton size="small" onClick={() => { logPageClickBi({ page_name: 'chat', button_name: 'minimize_chat' }); onMinimize(); }} aria-label={t('chat.minimize')} title={t('chat.minimizeShort')}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    </header>
    <DeleteConversationConfirm flow={flow} />
    <RenameConversationDialog flow={flow} />
    </>
  );
}
